/**
 * Printable A4 voucher sheet (add-voucher-system).
 *
 * Standalone route (no AppShell) — renders a batch as pages of 28 cards
 * (4×7), each a high-EC QR of the voucher code plus the human-readable code
 * and (optionally) the printed value. A reprint (opened via the Reprint button,
 * i.e. WITHOUT ?first=1) marks each card. `@media print` strips everything but
 * the grid and forces one batch-of-28 per physical page. The QR encodes the
 * bare code so a kiosk QR/keyboard-wedge scan assembles it exactly.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Button, QRCode, Spin, Switch } from "antd";
import { ApiError, api, type VoucherBatchPrint } from "../../api/client";
import { PRINT_COLS, PRINT_ROWS, VOUCHERS_PER_PAGE } from "./constants";

const euro = (c: number) => "€" + (c / 100).toFixed(2);

// Shared so the QR and the human-readable code line render at the SAME width.
// Sized so 4×7 = 28 cards fit one A4 sheet.
const QR_SIZE = 76;

function chunk<T>(arr: T[], n: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
}

export function VoucherPrintPage() {
  const { creationId } = useParams();
  const [searchParams] = useSearchParams();
  // Every open EXCEPT the one right after creating (which carries ?first=1) is a
  // reprint and gets a per-card marker (spec: "the original post-create print
  // does not").
  const isReprint = searchParams.get("first") !== "1";
  // Whether to print the € value on each voucher — a print-view toggle that
  // defaults OFF (a value-less voucher is safer if lost).
  const [showValue, setShowValue] = useState(false);
  const [data, setData] = useState<VoucherBatchPrint | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const id = Number(creationId);
    if (!Number.isFinite(id)) {
      setError("Invalid batch id");
      return;
    }
    api
      .getVoucherBatchPrint(id)
      .then(setData)
      .catch((e) =>
        setError(e instanceof ApiError ? e.message : "Failed to load batch"),
      );
  }, [creationId]);

  const pages = useMemo(
    () => (data ? chunk(data.vouchers, VOUCHERS_PER_PAGE) : []),
    [data],
  );

  if (error) return <div style={{ padding: 24 }}>{error}</div>;
  if (!data)
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <Spin />
      </div>
    );

  const dateStr = data.created_at
    ? new Date(data.created_at).toLocaleDateString()
    : "";

  return (
    <div className="voucher-print-root">
      <style>{`
        /* Print-first + theme-INDEPENDENT: force a black-on-white palette so the
           sheet is correct both as an on-screen preview (incl. dark mode) and on
           paper — never inherit the app's dark theme (which made text/QR white on
           the white sheet). */
        .voucher-print-root { background: #f3f4f6; padding: 16px; color: #111; }
        .voucher-print-toolbar { max-width: 800px; margin: 0 auto 16px; display: flex; gap: 12px; align-items: center; }
        .voucher-sheet {
          background: #fff; color: #000; width: 210mm; height: 297mm; margin: 0 auto 16px;
          padding: 10mm; box-sizing: border-box; box-shadow: 0 1px 6px rgba(0,0,0,.15);
          /* fixed height + flex column so the grid can stretch to fill the page
             (min-height doesn't give the flex child a definite height for 1fr) */
          display: flex; flex-direction: column;
        }
        .voucher-sheet-head { flex: 0 0 auto; font-size: 11px; color: #444; display: flex; justify-content: space-between; margin-bottom: 6mm; }
        /* Cutting grid: continuous faint guillotine lines (vertical + horizontal),
           NOT individual boxes. gap:0 keeps the lines unbroken across the sheet;
           each cell draws its right + bottom line, the grid draws the top + left,
           so the whole 4x6 grid is one set of straight cuts. Generous padding
           gives the white space between the content and the cut lines. */
        .voucher-grid {
          /* fill the remaining sheet height; equal rows distribute the matrix
             over the WHOLE page rather than packing at the top */
          flex: 1 1 auto;
          display: grid; grid-template-columns: repeat(${PRINT_COLS}, 1fr);
          grid-template-rows: repeat(${PRINT_ROWS}, 1fr);
          gap: 0;
        }
        .voucher-card {
          box-sizing: border-box;
          position: relative;
          padding: 3mm 4mm;
          text-align: center; display: flex; flex-direction: column; align-items: center;
          justify-content: center; break-inside: avoid; color: #000;
        }
        /* INTERNAL cutting guides only — NO outer edge (the paper edge is the
           outer cut). Faint dashed right line on every cell except the last
           column; dashed bottom line on every cell except the last row. */
        .voucher-card::after {
          content: ""; position: absolute; top: 0; right: 0; width: 1px; height: 100%;
          background: repeating-linear-gradient(to bottom, #cfcfcf 0 2px, transparent 2px 12px);
        }
        .voucher-card::before {
          content: ""; position: absolute; left: 0; bottom: 0; height: 1px; width: 100%;
          background: repeating-linear-gradient(to right, #cfcfcf 0 2px, transparent 2px 12px);
        }
        .voucher-card:nth-child(${PRINT_COLS}n)::after { content: none; }
        .voucher-card:nth-last-child(-n + ${PRINT_COLS})::before { content: none; }
        .voucher-card .code { font-size: 10px; margin-top: 5px; word-break: break-all; font-family: monospace; color: #000; }
        .voucher-card .val { font-size: 14px; font-weight: 700; margin-top: 3px; color: #000; }
        /* Reprint marker — a small faint glyph in the corner of each card. */
        .voucher-card .reprint-mark { position: absolute; top: 1.5mm; right: 2mm; font-size: 11px; line-height: 1; color: #9ca3af; }
        @media print {
          .voucher-print-root { background: #fff; padding: 0; color: #000; }
          .voucher-print-toolbar { display: none; }
          .voucher-sheet { box-shadow: none; margin: 0; }
          /* Page-break BETWEEN sheets only — not after the last (avoids a
             trailing blank page). */
          .voucher-sheet:not(:last-child) { break-after: page; }
          /* Force backgrounds/borders/QR to actually print. */
          .voucher-print-root, .voucher-sheet, .voucher-card {
            -webkit-print-color-adjust: exact; print-color-adjust: exact;
          }
          @page { size: A4; margin: 0; }
        }
      `}</style>

      <div className="voucher-print-toolbar">
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#111" }}>
          Voucher Batch #{data.creation_id}
        </h2>
        <span style={{ color: "#555" }}>
          {data.prefix} · {euro(data.amount_cents)} · {data.count} vouchers
        </span>
        <span
          style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#555" }}
        >
          <Switch size="small" checked={showValue} onChange={setShowValue} />
          Show value on vouchers
        </span>
        <Button type="primary" onClick={() => window.print()}>
          Print
        </Button>
      </div>

      {pages.map((page, pi) => (
        <div className="voucher-sheet" key={pi}>
          <div className="voucher-sheet-head">
            <span>
              {dateStr} · Batch #{data.creation_id} · {data.category_name || ""} ·{" "}
              {data.created_by_label}
            </span>
            <span>
              Page {pi + 1}/{pages.length}
            </span>
          </div>
          <div className="voucher-grid">
            {page.map((v) => (
              <div className="voucher-card" key={v.code}>
                {isReprint ? (
                  <span className="reprint-mark" title="reprint">
                    ↻
                  </span>
                ) : null}
                <QRCode
                  value={v.code}
                  type="svg"
                  color="#000000"
                  bgColor="#ffffff"
                  errorLevel="H"
                  size={QR_SIZE}
                  bordered={false}
                />
                <svg
                  className="code"
                  width={QR_SIZE}
                  height={11}
                  role="img"
                  aria-label={v.code}
                >
                  <text
                    x="50%"
                    y="9"
                    textAnchor="middle"
                    textLength={QR_SIZE - 6}
                    lengthAdjust="spacingAndGlyphs"
                    fontFamily="monospace"
                    fontSize="9"
                    fill="#000"
                  >
                    {v.code}
                  </text>
                </svg>
                {showValue ? (
                  <div className="val">{euro(v.amount_cents)}</div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default VoucherPrintPage;
